import React, { useCallback, useEffect } from 'react';
import DashboardCard from 'components/DashboardCard/DashboardCard';
import { Add, Pets } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from '@tanstack/react-router';
import { DataGrid } from '@mui/x-data-grid';
import { actions } from '../ClientView.redux';
import { Button } from '@mui/material';
import ClientPatientsEditModal from './ClientPatientsEditModal';
import NumberedDataGridPagination from 'components/NumberedDataGridPagination';

const ClientPatients = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { client_id } = useParams({ from: '/clients/view/$client_id' });
    const hasLoaded = useSelector((state) => state.clientView.patientsPreviouslyLoaded);
    const loading = useSelector((state) => state.clientView.patientsLoading);
    const patients = useSelector((state) => state.clientView.patients);
    const loadPatients = (clientID) => dispatch(actions.loadPatients(clientID));

    const columns = [
        { field: 'patient_name', headerName: 'Name', flex: 1, minWidth: 180, sortable: false },
        { field: 'species', headerName: 'Species', flex: 1, minWidth: 140, sortable: false },
        { field: 'breed', headerName: 'Breed', flex: 1, minWidth: 150, sortable: false },
        { field: 'gender', headerName: 'Gender', flex: 1, minWidth: 110, sortable: false },
        { field: 'is_neutered', headerName: 'Altered', flex: 1, minWidth: 110, sortable: false },
        { field: 'primary_clinic', headerName: 'Primary Clinic', flex: 1, minWidth: 180, sortable: false },
        { field: 'primary_vet', headerName: 'Primary Vet', flex: 1, minWidth: 170, sortable: false },
    ];

    const handleAddNew = useCallback(() => {
        dispatch(actions.openPatientsEditModal());
    }, [dispatch]);

    useEffect(() => {
        if (!hasLoaded && !loading) {
            loadPatients(client_id);
        }
    }, [loadPatients, client_id, hasLoaded, loading]);

    return (
        <DashboardCard
            color={'green'}
            icon={<Pets />}
            topContent={'Patients'}
            topLabel={
                <div>
                    <div>Click a patient to view or edit, or</div>
                    <div>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Add />}
                            onClick={handleAddNew}
                        >
                            Add New
                        </Button>
                    </div>
                </div>
            }
            size="xl"
        >
            <div style={{ width: '100%' }}>
                <DataGrid
                    rows={patients}
                    columns={columns}
                    getRowId={(row) => row.patient_id}
                    pagination
                    pageSizeOptions={[5, 10, 20, 50]}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 },
                        },
                    }}
                    loading={loading}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    disableCellFocus
                    sx={{
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        },
                    }}
                    slots={{ pagination: NumberedDataGridPagination }}
                    onRowClick={(params) => navigate({ to: `/patients/view/${params.row.patient_id}` })}
                    autoHeight
                />
            </div>

            <ClientPatientsEditModal />
        </DashboardCard>
    );
};

export default ClientPatients;
